import React, { useCallback, useEffect, useRef } from "react";
import {
  Animated,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import Colors from "../../../constants/colors";
import DownArrowIcon from "../../../../../../assets/images/DownArrowIcon.svg";

export type FloatingDropdownOption = {
  value: string;
  label: string;
};

type Props = {
  label?: string;
  /** Selected option value (used to float label). */
  value?: string;
  /** Text shown in the field; defaults to the matching option label. */
  displayValue?: string;
  options: FloatingDropdownOption[];
  onSelect: (value: string) => void;
  testID?: string;
  optionTestID?: string;
  errorText?: string | null;
  onClearError?: () => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const FloatingDropdownComponent: React.FC<Props> = ({
  label,
  value,
  displayValue,
  options,
  onSelect,
  testID,
  optionTestID,
  errorText,
  onClearError,
  isOpen: isOpenProp,
  onOpenChange,
}) => {
  const [isOpenInternal, setIsOpenInternal] = React.useState(false);
  const wrapperRef = useRef<View>(null);
  const isControlled = isOpenProp !== undefined;
  const isOpen = isControlled ? Boolean(isOpenProp) : isOpenInternal;

  const hasValue = Boolean(String(value ?? "").trim());
  const valueUpper = String(value ?? "").trim().toUpperCase();
  const selectedOption = options.find(
    (o) => String(o.value).trim().toUpperCase() === valueUpper
  );
  // Show label when options are loaded; fall back to raw value so a hydrated
  // code (e.g. AuthConsumer SenderCountry "USA") still appears before loadCountries returns.
  const fieldText =
    displayValue ??
    selectedOption?.label ??
    (hasValue ? String(value).trim() : "");

  /** Same trigger as FloatingTextInputComponent: focus/open or value. */
  const floatingLabelActive = isOpen || hasValue;

  const animated = useRef(new Animated.Value(floatingLabelActive ? 1 : 0)).current;

  useEffect(() => {
    Animated.timing(animated, {
      toValue: floatingLabelActive ? 1 : 0,
      duration: 180,
      useNativeDriver: false,
    }).start();
  }, [floatingLabelActive, animated]);

  const setOpen = useCallback(
    (open: boolean) => {
      if (!isControlled) setIsOpenInternal(open);
      onOpenChange?.(open);
    },
    [isControlled, onOpenChange],
  );

  useEffect(() => {
    if (!isOpen || Platform.OS !== "web") return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const node = wrapperRef.current as unknown as HTMLElement | null;
      const target = event.target as Node | null;
      if (node && target && !node.contains(target)) {
        setOpen(false);
      }
    };

    const timeoutId = window.setTimeout(() => {
      document.addEventListener("mousedown", handlePointerDown);
      document.addEventListener("touchstart", handlePointerDown);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isOpen, setOpen]);

  const toggleOpen = () => setOpen(!isOpen);

  const borderColor = errorText
    ? Colors.errorRedColor
    : isOpen
      ? Colors.black
      : Colors.textInputBackground;

  const borderWidth = 2;

  const labelStyle = {
    position: "absolute" as const,
    left: 12,
    top: animated.interpolate({
      inputRange: [0, 1],
      outputRange: [18, 6],
    }),
    fontSize: animated.interpolate({
      inputRange: [0, 1],
      outputRange: [16, 14],
    }),
    lineHeight: 20,
    color: animated.interpolate({
      inputRange: [0, 1],
      outputRange: [Colors.infoSubValue, Colors.infoSubValue],
    }),
    backgroundColor: "transparent",
    fontFamily: "Rubik",
  };

  return (
    <View ref={wrapperRef} style={[styles.wrapper, isOpen && styles.wrapperOpen]}>
      <View
        style={[
          styles.card,
          {
            borderColor,
            borderWidth,
            borderBottomLeftRadius: isOpen ? 0 : 8,
            borderBottomRightRadius: isOpen ? 0 : 8,
          },
        ]}
      >
        <TouchableOpacity
          testID={testID}
          style={styles.trigger}
          onPress={toggleOpen}
          activeOpacity={0.7}
        >
          <View pointerEvents="none" style={StyleSheet.absoluteFillObject}>
            <Animated.Text style={labelStyle} numberOfLines={1}>
              {label}
            </Animated.Text>
          </View>
          {floatingLabelActive ? (
            <View
              style={[
                styles.valueSlot,
                styles.valueSlotFloated,
              ]}
              pointerEvents="none"
            >
              <Text
                style={[
                  styles.valueText,
                  !fieldText && styles.valueTextPlaceholder,
                ]}
                numberOfLines={1}
              >
                {fieldText}
              </Text>
            </View>
          ) : null}
          <View style={styles.arrowWrap}>
            <DownArrowIcon width={16} height={16} />
          </View>
        </TouchableOpacity>
      </View>

      {isOpen && (
        <View style={[styles.listWrap, { borderColor, borderWidth }]}>
          <ScrollView style={styles.listScroll} nestedScrollEnabled keyboardShouldPersistTaps="handled">
            {options.map((opt) => (
              <TouchableOpacity
                key={opt.value}
                testID={optionTestID}
                style={styles.optionRow}
                onPress={() => {
                  if (errorText) onClearError?.();
                  onSelect(opt.value);
                  setOpen(false);
                }}
              >
                <Text style={styles.optionText}>{opt.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}

      {errorText ? <Text style={styles.errorText}>{errorText}</Text> : null}
    </View>
  );
};

export default FloatingDropdownComponent;

const styles = StyleSheet.create({
  wrapper: {
    marginVertical: 8,
  },
  wrapperOpen: {
    ...(Platform.OS === "web"
      ? ({
          position: "relative",
          zIndex: 10,
        } as object)
      : {}),
  },
  card: {
    borderRadius: 8,
    backgroundColor: Colors.textInputBackground,
    overflow: "hidden",
    position: "relative",
    ...(Platform.OS === "web"
      ? ({
          borderStyle: "solid",
          outline: "none",
          transitionProperty: "border-color, border-width",
          transitionDuration: "150ms",
          transitionTimingFunction: "ease-in-out",
        } as object)
      : {}),
  },
  trigger: {
    height: 56,
    position: "relative",
  },
  /** Mirrors FloatingTextInputComponent `input` + `inputFloatingLabelActive`. */
  valueSlot: {
    position: "absolute",
    left: 0,
    right: 40,
    top: 0,
    bottom: 0,
    height: "100%",
    paddingHorizontal: 12,
    paddingBottom: 6,
  },
  valueSlotFloated: {
    paddingTop: Platform.OS === "web" ? 24 : 16,
  },
  valueText: {
    lineHeight: 24,
    fontSize: 16,
    fontWeight: "400",
    fontFamily: "Rubik",
    color: Colors.black,
    ...(Platform.OS === "web"
      ? ({
          display: "block",
        } as object)
      : {}),
  },
  valueTextPlaceholder: {
    color: Colors.infoSubValue,
  },
  arrowWrap: {
    position: "absolute",
    right: 12,
    top: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  listWrap: {
    maxHeight: 240,
    borderTopWidth: 0,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    backgroundColor: Colors.textInputBackground,
    overflow: "hidden",
  },
  listScroll: {
    maxHeight: 240,
  },
  optionRow: {
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  optionText: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: "Rubik",
    fontWeight: "400",
    color: Colors.infoSubValue,
  },
  errorText: {
    fontSize: 14,
    fontFamily: "Rubik",
    fontWeight: "400",
    lineHeight: 20,
    color: Colors.errorRedColor,
    marginTop: 4,
  },
});
